<script setup lang="ts">
import MiscCarousel from '~/components/misc/MiscCarousel.vue'

export interface StoreFitter {
    image?: { url?: string; alt?: string } | null
    name?: string | null
    phone?: string | null
    email?: string | null
    url?: string | null
}

interface FittingOption {
    name?: string | null
    description?: string | null
    link?: string | null
    linkText?: string | null
}

interface StoreManufacturer {
    id: string
    media?: { url?: string; alt?: string } | null
    link?: string | null
}

export interface Store {
    type: 'internal' | 'external'
    name?: string | null
    street?: string | null
    zipcode?: string | null
    city?: string | null
    country?: string | null
    image?: { url?: string; alt?: string } | null
    fittingOptions: FittingOption[]
    manufacturers: StoreManufacturer[]
    fitters: StoreFitter[]
    showTitleInModal: boolean
    showModalSubtitle: boolean
    modalSubtitle: string | null
}

const props = defineProps<{
    store: Store
}>()

const fitterSlides = computed(() => props.store.fitters.map((_, index) => ({ id: index })))
const manufacturerSlides = computed(() => props.store.manufacturers.map(manufacturer => ({ id: manufacturer.id })))

const storeModalEl = ref<HTMLDialogElement | null>(null)
const showModal = ref(false)

function validateData(data: unknown): data is string {
    return typeof data === 'string' && data.trim() !== ''
}

function onClickBookButton() {
    showModal.value = true
    setTimeout(() => {
        storeModalEl.value?.showModal()
    }, 100)
}

function onClickCloseModal() {
    storeModalEl.value?.close()
    setTimeout(() => {
        showModal.value = false
    }, 100)
}
</script>

<template>
    <div class="font-sans! text-base!">
        <div class="flex items-center gap-3 p-3">
            <MiscImage
                v-if="validateData(store?.image?.url)"
                :src="store.image!.url!"
                :alt="store?.image?.alt"
                width="400"
                class="flex-shrink w-[45%] max-w-[400px] h-[195px] object-cover"
            />
            <div class="w-full max-w-[250px] flex flex-col gap-3 p-[10px] pr-0">
                <div
                    v-if="validateData(store?.name)"
                    class="text-lg font-bold uppercase"
                >
                    {{ store.name }}
                </div>
                <div class="text-xs">
                    <div v-if="validateData(store?.street)">
                        {{ store.street }}
                    </div>
                    <div v-if="validateData(store?.zipcode) && validateData(store?.city)">
                        {{ store.zipcode }} {{ store.city }}
                    </div>
                    <div v-if="validateData(store?.country)">
                        {{ store.country }}
                    </div>
                </div>
                <button
                    type="button"
                    class="btn btn-accent"
                    @click="onClickBookButton()"
                >
                    {{ $t('fitting.bookFitting') }}
                </button>
            </div>
        </div>

        <ClientOnly>
            <dialog
                v-if="showModal"
                ref="storeModalEl"
                class="modal"
                @close="onClickCloseModal()"
            >
                <div class="modal-box relative px-6 pt-11 pb-6 w-full max-w-[1280px]">
                    <button
                        type="button"
                        class="btn btn-sm btn-circle bth-ghost absolute top-3 right-3"
                        @click="onClickCloseModal()"
                    >
                        <MiscIcon
                            name="x"
                            size="sm"
                        />
                    </button>

                    <div v-if="store.type === 'internal'">
                        <div
                            v-if="store.showTitleInModal"
                            class="text-3xl font-bold text-center mb-6"
                        >
                            {{ $t('fitting.chooseOptionHeadline') }}
                        </div>

                        <!-- eslint-disable-next-line vue/no-v-html -->
                        <div v-if="store.showModalSubtitle && store.modalSubtitle" class="text-center mx-auto mb-10 max-w-[830px]" v-html="store.modalSubtitle" />

                        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <div
                                v-for="(option, optionId) in store.fittingOptions"
                                :key="`option-${optionId}`"
                                class="w-full p-4 bg-base-200 flex flex-col gap-6"
                            >
                                <div
                                    v-if="validateData(option?.name)"
                                    class="text-2xl font-semibold uppercase"
                                >
                                    {{ option.name }}
                                </div>
                                <div
                                    v-if="validateData(option?.description)"
                                    class="text-sm"
                                >
                                    {{ option.description }}
                                </div>
                                <a
                                    v-if="validateData(option?.link) && validateData(option?.linkText)"
                                    :href="option.link"
                                    class="btn btn-accent w-full mt-auto"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >{{ option.linkText }}</a>
                            </div>
                        </div>
                    </div>

                    <div v-if="store.type === 'external'">
                        <div
                            v-if="store.showTitleInModal"
                            class="text-3xl font-bold text-center mb-12"
                        >
                            {{ $t('fitting.chooseFitterHeadline') }}
                        </div>
                        <div class="text-lg mb-12 text-center max-w-[830px] m-auto">
                            {{ $t('fitting.chooseFitterText') }}
                        </div>
                        <MiscCarousel
                            v-if="store.fitters.length > 0"
                            :slides="fitterSlides"
                            :label="$t('fitting.chooseFitterHeadline')"
                            :enable-autoplay="false"
                            :enable-infinite-scroll="false"
                            :gap="24"
                            carousel-width="100%"
                            :breakpoints="{
                                default: { slidesPerView: 1 },
                                sm: { slidesPerView: 2 }
                            }"
                            class="flex lg:!hidden"
                        >
                            <template #default="{ index }">
                                <StoreFinderStoreFitter
                                    v-if="store.fitters[index] != null"
                                    :fitter="store.fitters[index]!"
                                    class="mb-4"
                                />
                            </template>
                        </MiscCarousel>

                        <div class="hidden lg:flex gap-6">
                            <StoreFinderStoreFitter
                                v-for="(fitter, fitterIndex) in store.fitters"
                                :key="fitterIndex"
                                :fitter="fitter"
                                class="w-full"
                            />
                        </div>

                        <div
                            v-if="store.manufacturers.length > 0"
                            class="flex flex-col gap-6 mt-6 lg:mt-12"
                        >
                            <div class="font-fjalla-one text-2xl uppercase">
                                {{ $t('fitting.fittingManufacturersHeadline') }}
                            </div>
                            <MiscCarousel
                                :slides="manufacturerSlides"
                                :label="$t('fitting.fittingManufacturersHeadline')"
                                :enable-autoplay="false"
                                :enable-infinite-scroll="false"
                                :enable-dots="false"
                                :gap="24"
                                carousel-width="100%"
                                :breakpoints="{
                                    default: { slidesPerView: 2.5 },
                                    sm: { slidesPerView: 3 },
                                    md: { slidesPerView: 3.5 },
                                    lg: { slidesPerView: 5 },
                                    xl: { slidesPerView: 6 }
                                }"
                                class="w-full"
                            >
                                <template #default="{ index }">
                                    <MiscLink
                                        v-if="store.manufacturers[index]?.link != null"
                                        :to="store.manufacturers[index]!.link!"
                                        class="w-full h-[120px] bg-base-200 flex justify-center items-center p-3"
                                    >
                                        <MiscImage
                                            :src="store.manufacturers[index]?.media?.url"
                                            :alt="store.manufacturers[index]?.media?.alt"
                                            height="70"
                                            class="h-[70px]"
                                        />
                                    </MiscLink>
                                    <MiscImage
                                        v-else-if="store.manufacturers[index]?.media?.url != null"
                                        :src="store.manufacturers[index]!.media!.url!"
                                        :alt="store.manufacturers[index]?.media?.alt"
                                        height="70"
                                        class="h-[70px]"
                                    />
                                </template>
                            </MiscCarousel>
                        </div>
                    </div>
                </div>
                <form
                    method="dialog"
                    class="modal-backdrop"
                    @submit.prevent="onClickCloseModal()"
                >
                    <button type="submit">
                        {{ $t('layout.header.close') }}
                    </button>
                </form>
            </dialog>
        </ClientOnly>
    </div>
</template>
